import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import Icon from 'components/Icon';
import Loading from 'components/Loading';
import {
  POSCard,
  POSCardHeader,
  POSCardBody,
  POSDatePill,
} from 'components/POS';
import { usePrintManager } from 'hooks/usePrintManager';
import { AppContext } from 'shared/AppContext';
import { useCart } from 'shared/CartContext';
import { useGetAccount } from '../Accounts/gql/queries/useGetAccount';
import { useGetEvent } from '../Events/EventsMenu/gql/queries/get-event';
import { CartList } from './Components/CartList';
import { EmptyCart } from './Components/EmptyCart';
import { PaymentOption } from './Components/PaymentOption';
import { ReaderIpModal } from './Components/ReaderIpModal';
import { TicketListV2 } from './Components/TicketListV2';

// Main container with gradient background - fixed height to prevent overflow
const MainContainer = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  grid-template-rows: 1fr;
  gap: 24px;
  padding: 24px;
  height: 100dvh;
  max-height: 100dvh;
  overflow: hidden;
  box-sizing: border-box;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.background?.primary ||
        'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)'
      : '#f8fafc'};
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#000033'};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, sans-serif;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
    padding: 16px;
    height: auto;
    min-height: 100dvh;
    max-height: none;
    overflow: auto;
  }
`;

// Event header section
const EventInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const EventName = styled.h1`
  font-size: 22px;
  font-weight: 700;
  margin: 0;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#000033'};
`;

// Cart section container - fills available height
const CartSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
  max-height: 100%;
  overflow: hidden;

  @media (max-width: 1024px) {
    max-height: none;
    min-height: 400px;
  }
`;

// Floating cart button for mobile
const FloatingCartButton = styled.button`
  position: fixed;
  bottom: 15px;
  right: 15px;
  background-color: ${(props) => props.theme.colors?.primary || '#fa4616'};
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  font-size: 18px;
  font-weight: bold;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  cursor: pointer;

  @media (min-width: 1025px) {
    display: none;
  }
`;

const CountBadge = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  background-color: white;
  color: ${(props) => props.theme.colors?.primary || '#fa4616'};
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 14px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Ticket card with full height - scrollable content
const TicketCard = styled(POSCard)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 100%;
  overflow: hidden;

  @media (max-width: 1024px) {
    min-height: 400px;
    max-height: none;
    flex: none;
  }
`;

// Cart card that fills available space
const CartCard = styled(POSCard)`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CartPage = () => {
  const params = useParams();
  const location = useLocation();
  const EventId = parseInt(params.id);

  const {
    ticketCount,
    setSeatId,
    setAccountId,
    accountId,
    event,
    setEvent,
    setAccount,
    tickets,
    setSelected,
  } = useCart();

  const { data, loading } = useGetEvent(EventId);

  const {
    state: { account },
  } = useContext(AppContext);

  const { data: accountData } = useGetAccount(
    event?.account_id,
    !event || account
  );

  const [isCheckoutVisible, setIsCheckoutVisible] = useState(false);

  const urlParams = new URLSearchParams(location.search);
  const payment_intent = urlParams.get('payment_intent');
  const payment_id = urlParams.get('payment_intent_client_secret');

  const { isConnected } = usePrintManager();
  const [isReaderIpVisible, setIsReaderIpVisible] = useState(false);

  useEffect(() => {
    // Clear storage initially
    localStorage.removeItem('tickets');
    localStorage.removeItem('selectedTicket');

    // Remove any old timer keys
    Object.keys(localStorage).forEach((key) => {
      if (key.includes('timer')) localStorage.removeItem(key);
    });
  }, []);

  useEffect(() => {
    if (data && data.getEvent) {
      setEvent(data.getEvent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (accountData && accountData.getAccount) {
      setAccount(accountData.getAccount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountData]);

  useEffect(() => {
    if (event && event.seats_id) {
      setSeatId(event.seats_id);
    }

    if (event && event.account_id) {
      setAccountId(event.account_id);
    }
  }, [data, event, setSeatId, setAccountId]);

  useEffect(() => {
    // Check if payment_intent and payment_id are in the url and open payment option modal if they are
    if (payment_intent && payment_id && accountId) {
      setIsCheckoutVisible(true);
    }
  }, [payment_intent, payment_id, accountId]);

  const connectionStatus = useMemo(() => {
    return isConnected();
  }, [isConnected]);

  useEffect(() => {
    const isPrintRequired =
      event?.pos_settings?.ticket_delivery === 'print' ||
      event?.pos_settings?.receipt_delivery === 'print';

    if (isPrintRequired && !connectionStatus) {
      setIsReaderIpVisible(true);
    } else {
      setIsReaderIpVisible(false);
    }
  }, [connectionStatus, event]);

  // Reset selected ticket on initial load
  useEffect(() => {
    setSelected(Object.keys(tickets)[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cartRef = React.useRef(null);

  const scrollToCart = () => {
    // eslint-disable-next-line no-unused-expressions
    cartRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading && !data) {
    return <Loading />;
  }

  return (
    <EnhancedThemeProvider>
      <MainContainer>
        {/* Event & Tickets Card */}
        <TicketCard>
          <POSCardHeader>
            <EventInfo>
              <POSDatePill
                startDate={event?.start_date}
                endDate={event?.end_date}
                isMultiDay={event?.isMultiDay}
              />
              <EventName>{event?.name}</EventName>
            </EventInfo>
          </POSCardHeader>

          <POSCardBody noPadding>
            <TicketListV2 />
          </POSCardBody>
        </TicketCard>

        {/* Cart Section */}
        <CartSection ref={cartRef}>
          {ticketCount === 0 ? (
            <CartCard>
              <EmptyCart event={event} />
            </CartCard>
          ) : (
            <CartList />
          )}
        </CartSection>

        {/* Floating Cart Button for Mobile */}
        {ticketCount > 0 && (
          <CartFloatingButton count={ticketCount} onClick={scrollToCart} />
        )}

        <PaymentOption
          isVisible={isCheckoutVisible}
          setIsVisible={setIsCheckoutVisible}
          manualCardPayment={!!payment_intent}
          accountIdPayment={accountId}
        />

        <ReaderIpModal
          isVisible={isReaderIpVisible}
          setIsVisible={setIsReaderIpVisible}
        />
      </MainContainer>

      {/* Hidden logo for printing */}
      <img
        id="logo"
        src="/images/th-simple-logo.png"
        width="200"
        height="100"
        style={{ display: 'none' }}
        alt="print-logo"
      />
    </EnhancedThemeProvider>
  );
};

const CartFloatingButton = ({ count, onClick }) => {
  if (count === 0) return null;

  return (
    <FloatingCartButton onClick={onClick}>
      <Icon icon="shopping_cart" size="28" />
      <CountBadge>{count}</CountBadge>
    </FloatingCartButton>
  );
};

export default CartPage;
